import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

const SESSION_MAX_MS = 24 * 60 * 60 * 1000; // 24 h

/**
 * Prazo para o serviço de autenticação dizer alguma coisa na partida.
 *
 * POR QUE ISTO EXISTE
 * -------------------
 *
 * Com o token expirado, o cliente do Supabase tenta renovar antes de devolver a
 * sessão. Se o serviço de autenticação estiver fora do ar, essa renovação não
 * responde nem falha — fica pendurada. `loading` seguia `true` para sempre, e a
 * pessoa via uma tela em branco com um spinner, sem nenhuma indicação de que
 * algo tinha dado errado. Aconteceu de verdade, em produção.
 *
 * É o mesmo princípio do service worker: **uma requisição que falhou não pode
 * chegar à tela parecendo que ainda está a caminho.** Espera sem fim é a pior
 * forma de erro, porque não dá nem para saber que houve um.
 */
const PARTIDA_MAX_MS = 8000;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** O serviço de autenticação não respondeu na partida. */
  indisponivel: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  indisponivel: false,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [indisponivel, setIndisponivel] = useState(false);
  const sessionStartRef = useRef<number | null>(null);

  useEffect(() => {
    // Se o serviço responder depois do prazo, o relógio não pode desfazer o que
    // ele trouxe — daí a trava, e não um simples `setLoading(false)`.
    let respondeu = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      respondeu = true;
      setIndisponivel(false);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session && sessionStartRef.current === null) {
        sessionStartRef.current = Date.now();
      } else if (!session) {
        sessionStartRef.current = null;
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      respondeu = true;
      setIndisponivel(false);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session && sessionStartRef.current === null) {
        sessionStartRef.current = Date.now();
      }
    }).catch(() => {
      respondeu = true;
      setLoading(false);
      setIndisponivel(true);
    });

    const relogio = setTimeout(() => {
      if (respondeu) return;
      // Sem sessão confirmada: o app segue como deslogado, e a tela diz por quê.
      // Fingir que ainda carrega seria esconder a falha atrás de uma animação.
      setLoading(false);
      setIndisponivel(true);
    }, PARTIDA_MAX_MS);

    return () => {
      clearTimeout(relogio);
      subscription.unsubscribe();
    };
  }, []);

  // Auto-logout after SESSION_MAX_MS (24 h)
  useEffect(() => {
    if (!user) return;
    const start = sessionStartRef.current ?? Date.now();
    const remaining = SESSION_MAX_MS - (Date.now() - start);
    if (remaining <= 0) { supabase.auth.signOut(); return; }
    const timer = setTimeout(() => supabase.auth.signOut(), remaining);
    return () => clearTimeout(timer);
  }, [user]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, indisponivel, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
